// In C:\Users\clint\f1-tipping-site\frontend\src\components\pages\AdminPanel.js
import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';

function AdminPanel() {
  const [races, setRaces] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [raceScores, setRaceScores] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [selectedScore, setSelectedScore] = useState(null); // Add state for selected score details
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('results'); // 'results', 'leaderboard', 'raceScores', 'scoreDetails', 'headToHead'
  const [raceResult, setRaceResult] = useState(null);
  const [calculatingScores, setCalculatingScores] = useState(false);
  const [calculateScoreResult, setCalculateScoreResult] = useState(null);
  const [forceRecalculate, setForceRecalculate] = useState({});

  // Add this function at the top of your file
function transformRaceResult(result) {
  if (!result) return result;
  
  // Create a new object with the structure the UI expects
  const transformed = { ...result };
  
  // Add driverHeadToHead property if headToHead.drivers exists
  if (result.headToHead && result.headToHead.drivers) {
    transformed.driverHeadToHead = {
      driver1: result.headToHead.drivers.driver1?.name || result.headToHead.drivers.driver1?.driverId,
      driver2: result.headToHead.drivers.driver2?.name || result.headToHead.drivers.driver2?.driverId,
      winner: result.headToHead.drivers.winner
    };
  }
  
  // Add teamHeadToHead property if headToHead.teams exists
  if (result.headToHead && result.headToHead.teams) {
    transformed.teamHeadToHead = {
      team1: result.headToHead.teams.team1?.name || result.headToHead.teams.team1?.constructorId,
      team2: result.headToHead.teams.team2?.name || result.headToHead.teams.team2?.constructorId,
      winner: result.headToHead.teams.winner
    };
  }
  
  return transformed;
}

  // New states for head-to-head configuration
    const [drivers, setDrivers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [editingRace, setEditingRace] = useState(null);
    const [headToHeadConfig, setHeadToHeadConfig] = useState({
    drivers: {
        driver1: { driverId: '', code: '', name: '' },
        driver2: { driverId: '', code: '', name: '' }
    },
    teams: {
        team1: { constructorId: '', name: '' },
        team2: { constructorId: '', name: '' }
    }
    });

  useEffect(() => {
    const fetchData = async () => {
        try {
          setLoading(true);
          // Fetch races
          const racesResponse = await apiClient.get(`/api/f1data/races/all`);
          console.log('Races data:', racesResponse.data); // Add this to see the data structure
          setRaces(racesResponse.data);
        
        // Fetch leaderboard
        const leaderboardResponse = await apiClient.get(`/api/scores/leaderboard`);
        setLeaderboard(leaderboardResponse.data);

         // Fetch drivers - add detailed logging
        const driversResponse = await apiClient.get(`/api/f1data/drivers`);
        console.log('Drivers data:', driversResponse.data);
        console.log('Driver data structure sample:', driversResponse.data[0]);
        setDrivers(driversResponse.data);

         // Fetch teams - add detailed logging
        const teamsResponse = await apiClient.get(`/api/f1data/teams`);
        console.log('Teams data:', teamsResponse.data);
        console.log('Team data structure sample:', teamsResponse.data[0]);
        setTeams(teamsResponse.data);
        
        setError(null);
        
        // Add log after setting state
        console.log('After setting state - drivers length:', drivers.length);
        console.log('After setting state - teams length:', teams.length);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

 const processResults = async (raceId, raceName, forceRecalc = false) => {
  try {
    setLoading(true);
    setMessage('');
    setError('');
    
    const url = forceRecalc 
      ? `/api/results/process/${raceId}?force=true`
      : `/api/results/process/${raceId}`;
    
    console.log(`Processing results for ${raceName}${forceRecalc ? ' (FORCE RECALCULATE)' : ''}`);
    
    const response = await apiClient.post(url);
    
    const message = forceRecalc 
      ? `Scores RECALCULATED for ${raceName}. ${response.data.scores} predictions updated with new scoring logic.`
      : `Results processed for ${raceName}. ${response.data.scores} predictions scored.`;
    
    setMessage(message);
    
    // If we're currently viewing scores for this race, refresh them
    if (selectedRace === raceName) {
      await fetchRaceScores(raceId, raceName);
    }
    
    // Refresh leaderboard after processing
    const leaderboardResponse = await apiClient.get(`/api/scores/leaderboard`);
    setLeaderboard(leaderboardResponse.data);
  } catch (err) {
    console.error('Error processing results:', err);
    setError(err.response?.data?.message || 'Failed to process results');
  } finally {
    setLoading(false);
  }
};

  const fetchRaceScores = async (raceId, raceName) => {
    if (!raceId) {
      console.error('No race ID provided');
      setError('Cannot fetch scores: Race ID is missing');
      return;
    }
    
    console.log(`Fetching scores for race: ${raceId} (${raceName})`);
    
    try {
      setLoading(true);
      setError('');
      
      const url = `/api/scores/race/${raceId}`;
      console.log('Making API request to:', url);
      
      const response = await apiClient.get(url);
      console.log('API response received:', response);
      
      if (response.data && response.data.scores) {
        setRaceScores(response.data.scores);
        setRaceResult(transformRaceResult(response.data.result));
        setSelectedRace(raceName);
        setActiveTab('raceScores');
      } else {
        console.error('Unexpected API response format:', response.data);
        setError('Received invalid data format from server');
      }
    } catch (err) {
      console.error('Error fetching race scores:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load race scores';
      console.error('Error details:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
    console.log('Fetching scores for race with ID:', raceId);
  };

  //New function for calculate scores
  const calculateAllScores = async () => {
    try {
      setCalculatingScores(true);
      setError('');
      setMessage('');
      setCalculateScoreResult(null);
      
      // Call our API endpoint to calculate all scores
      const response = await apiClient.post(`/api/scores/calculate-all-scores`);
      
      setCalculateScoreResult(response.data);
      setMessage('All user scores calculated and leaderboard updated successfully!');
      
      // Refresh leaderboard after calculation
      const leaderboardResponse = await apiClient.get(`/api/scores/leaderboard`);
      setLeaderboard(leaderboardResponse.data);
    } catch (err) {
      console.error('Error calculating scores:', err);
      setError(err.response?.data?.message || 'Failed to calculate user scores');
    } finally {
      setCalculatingScores(false);
    }
  };

  // New function to edit head-to-head for a race
const editHeadToHead = async (race) => {
    setEditingRace(race);
    
    // Initialize with existing values if they exist
    if (race.headToHead) {
      setHeadToHeadConfig(race.headToHead);
    } else {
      // Reset to defaults
      setHeadToHeadConfig({
        drivers: {
          driver1: { driverId: '', code: '', name: '' },
          driver2: { driverId: '', code: '', name: '' }
        },
        teams: {
          team1: { constructorId: '', name: '' },
          team2: { constructorId: '', name: '' }
        }
      });
    }
    console.log('Editing head-to-head for race with ID:', race._id);
    console.log('Editing race:', race);
    console.log('Available drivers:', drivers);
    console.log('Available teams:', teams);
    setActiveTab('headToHead');
  };
  
  // Handler for driver selection in head-to-head
  const handleDriverSelect = (position, driverId) => {
    console.log('All drivers:', drivers);
    console.log('Looking for driver with ID:', driverId);
    
    const selectedDriver = drivers.find(d => d.driverId === driverId);
    console.log('Selected driver:', selectedDriver);
    
    if (selectedDriver) {
      setHeadToHeadConfig({
        ...headToHeadConfig,
        drivers: {
          ...headToHeadConfig.drivers,
          [position]: {
            driverId: selectedDriver.driverId,
            code: selectedDriver.code,
            name: `${selectedDriver.givenName} ${selectedDriver.familyName}`
          }
        }
      });
    }
  };
  
  // Handler for team selection in head-to-head
  const handleTeamSelect = (position, constructorId) => {
    const selectedTeam = teams.find(t => t.constructorId === constructorId);
    
    if (selectedTeam) {
      setHeadToHeadConfig({
        ...headToHeadConfig,
        teams: {
          ...headToHeadConfig.teams,
          [position]: {
            constructorId: selectedTeam.constructorId,
            name: selectedTeam.name
          }
        }
      });
    }
  };
  
// Save head-to-head configuration
const saveHeadToHead = async () => {
  if (!editingRace) return;
  
  try {
    setLoading(true);
    setError('');
    setMessage('');
    
    // Validate that all selections are made
    if (!headToHeadConfig.drivers.driver1.driverId || 
        !headToHeadConfig.drivers.driver2.driverId ||
        !headToHeadConfig.teams.team1.constructorId ||
        !headToHeadConfig.teams.team2.constructorId) {
      setError('Please select all drivers and teams for head-to-head');
      setLoading(false);
      return;
    }
    
    // Check if driver1 and driver2 are different
    if (headToHeadConfig.drivers.driver1.driverId === headToHeadConfig.drivers.driver2.driverId) {
      setError('Please select different drivers for head-to-head');
      setLoading(false);
      return;
    }
    
    // Check if team1 and team2 are different
    if (headToHeadConfig.teams.team1.constructorId === headToHeadConfig.teams.team2.constructorId) {
      setError('Please select different teams for head-to-head');
      setLoading(false);
      return;
    }
    
    // Make sure all driver and team properties are fully populated
    const selectedDriver1 = drivers.find(d => d.driverId === headToHeadConfig.drivers.driver1.driverId);
    const selectedDriver2 = drivers.find(d => d.driverId === headToHeadConfig.drivers.driver2.driverId);
    const selectedTeam1 = teams.find(t => t.constructorId === headToHeadConfig.teams.team1.constructorId);
    const selectedTeam2 = teams.find(t => t.constructorId === headToHeadConfig.teams.team2.constructorId);
    
    // Create complete head-to-head configuration with all needed properties
    const completeHeadToHeadConfig = {
      drivers: {
        driver1: {
          driverId: selectedDriver1.driverId,
          code: selectedDriver1.code,
          name: `${selectedDriver1.givenName} ${selectedDriver1.familyName}`
        },
        driver2: {
          driverId: selectedDriver2.driverId,
          code: selectedDriver2.code,
          name: `${selectedDriver2.givenName} ${selectedDriver2.familyName}`
        }
      },
      teams: {
        team1: {
          constructorId: selectedTeam1.constructorId,
          name: selectedTeam1.name
        },
        team2: {
          constructorId: selectedTeam2.constructorId,
          name: selectedTeam2.name
        }
      }
    };
    
    console.log('Saving complete head-to-head configuration:', completeHeadToHeadConfig);
    
    // Update the race with the head-to-head config
    const response = await apiClient.patch(
      `/api/f1data/races/${editingRace._id}`,
      { headToHead: completeHeadToHeadConfig }
    );
    
    if (response.data) {
      // Update the local races array
      const updatedRaces = races.map(race => 
        race._id === editingRace._id 
          ? { ...race, headToHead: completeHeadToHeadConfig } 
          : race
      );
      setRaces(updatedRaces);
      setHeadToHeadConfig(completeHeadToHeadConfig);
      
      setMessage('Head-to-head configuration saved successfully');
      setActiveTab('results');
    }
  } catch (err) {
    console.error('Error saving head-to-head config:', err);
    setError(err.response?.data?.message || 'Failed to save head-to-head configuration');
  } finally {
    setLoading(false);
  }
};

  // Normalize driver names for display
  const normalizeDriverNameForDisplay = (driverName) => {
    if (!driverName) return 'N/A';
    
    // Convert underscores to spaces
    driverName = driverName.replace(/_/g, ' ');
    
    // Capitalize first letters
    return driverName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // New function to view score details
  const viewScoreDetails = (score) => {
    setSelectedScore(score);
    setActiveTab('scoreDetails');
  };

  const renderScoreBreakdown = (breakdown) => {
    if (!breakdown || breakdown.length === 0) {
      return <p>No breakdown available</p>;
    }

    // Group by prediction type
    const byType = {};
    breakdown.forEach(item => {
      if (!byType[item.type]) byType[item.type] = [];
      byType[item.type].push(item);
    });

    return (
      <div className="space-y-3">
        {Object.entries(byType).map(([type, items]) => {
          const totalForType = items.reduce((sum, item) => sum + item.points, 0);
          
          return (
            <div key={type} className="p-3 bg-gray-50 rounded">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">
                  {type.replace(/_/g, ' ')}:
                </h4>
                <span className="font-bold text-blue-600">+{totalForType} pts</span>
              </div>
              <div className="mt-1">
                {items.map((item, i) => (
                  <div key={i} className="text-sm text-gray-700 flex justify-between">
                    <span>{item.driver}</span>
                    <span>+{item.points} pt{item.points !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // MOVED INSIDE: renderCompleteBreakdown is now inside the component
      const renderCompleteBreakdown = (score, result) => {
        if (!score || !score.prediction) {
          return <p className="text-gray-500">Complete prediction data not available</p>;
        }

        const predictions = score.prediction.predictions;
        
        return (
          <div className="space-y-6">
            {/* Podium Predictions */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 font-medium">Podium Predictions</div>
              <div className="p-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prediction</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actual Result</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Points</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-3 py-2 whitespace-nowrap text-black">P1</td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">
                        {normalizeDriverNameForDisplay(predictions.podium[0])}
                        {score.breakdown.some(b => b.type === 'WINNER' && b.driver === predictions.podium[0]) ? 
                          <span className="ml-2 text-green-600">✓</span> : 
                          <span className="ml-2 text-red-600">✗</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">{normalizeDriverNameForDisplay(result.podium[0])}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-black">
                        {score.breakdown.find(b => b.type === 'WINNER' && b.driver === predictions.podium[0])?.points || 0}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 whitespace-nowrap text-black">P2</td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">
                        {normalizeDriverNameForDisplay(predictions.podium[1])}
                        {score.breakdown.some(b => b.type === 'SECOND_PLACE' && b.driver === predictions.podium[1]) ? 
                          <span className="ml-2 text-green-600">✓</span> : 
                          <span className="ml-2 text-red-600">✗</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">{normalizeDriverNameForDisplay(result.podium[1])}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-black">
                        {score.breakdown.find(b => b.type === 'SECOND_PLACE' && b.driver === predictions.podium[1])?.points || 0}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 whitespace-nowrap text-black">P3</td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">
                        {normalizeDriverNameForDisplay(predictions.podium[2])}
                        {score.breakdown.some(b => b.type === 'THIRD_PLACE' && b.driver === predictions.podium[2]) ? 
                          <span className="ml-2 text-green-600">✓</span> : 
                          <span className="ml-2 text-red-600">✗</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">{normalizeDriverNameForDisplay(result.podium[2])}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-black">
                        {score.breakdown.find(b => b.type === 'THIRD_PLACE' && b.driver === predictions.podium[2])?.points || 0}
                      </td>
                    </tr>
                    {/* Podium Driver but Wrong Position */}
                    {score.breakdown.filter(b => b.type === 'PODIUM_DRIVER').map((item, idx) => (
                      <tr key={`podium-driver-${idx}`} className="bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap text-black">Podium (any pos)</td>
                        <td className="px-3 py-2 whitespace-nowrap text-black">
                          {normalizeDriverNameForDisplay(item.driver)}
                          <span className="ml-2 text-green-600">✓</span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-black">In podium</td>
                        <td className="px-3 py-2 whitespace-nowrap text-black text-right">{item.points}</td>
                      </tr>
                    ))}
                    
                    {/* ADD BONUS POINTS ROWS */}
                    {/* All 3 Podium Correct Order Bonus */}
                    {score.breakdown.filter(b => b.type === 'ALL_PODIUM_CORRECT_ORDER').map((item, idx) => (
                      <tr key={`bonus-correct-order-${idx}`} className="bg-yellow-50 border-t-2 border-yellow-200">
                        <td className="px-3 py-2 whitespace-nowrap text-black font-bold" colSpan="3">
                          🎯 BONUS: All 3 drivers in correct order
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-black text-right font-bold text-yellow-600">
                          +{item.points}
                        </td>
                      </tr>
                    ))}
                    
                    {/* All 3 Podium Wrong Order Bonus */}
                    {score.breakdown.filter(b => b.type === 'ALL_PODIUM_WRONG_ORDER').map((item, idx) => (
                      <tr key={`bonus-wrong-order-${idx}`} className="bg-yellow-50 border-t-2 border-yellow-200">
                        <td className="px-3 py-2 whitespace-nowrap text-black font-bold" colSpan="3">
                          🎯 BONUS: All 3 drivers but wrong order
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-black text-right font-bold text-yellow-600">
                          +{item.points}
                        </td>
                      </tr>
                    ))}
                    
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="3" className="px-3 py-2 text-right font-medium text-black">Podium Points:</td>
                      <td className="px-3 py-2 text-right font-bold text-black">
                        {score.breakdown
                          .filter(item => ['WINNER', 'SECOND_PLACE', 'THIRD_PLACE', 'PODIUM_DRIVER', 'ALL_PODIUM_CORRECT_ORDER', 'ALL_PODIUM_WRONG_ORDER'].includes(item.type))
                          .reduce((sum, item) => sum + item.points, 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Other Predictions */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 font-medium">Other Predictions</div>
              <div className="p-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prediction</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actual Result</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Points</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* Pole Position */}
                    <tr>
                      <td className="px-3 py-2 whitespace-nowrap text-black">Pole Position</td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">
                        {normalizeDriverNameForDisplay(predictions.polePosition)}
                        {score.breakdown.some(b => b.type === 'POLE_POSITION' && b.driver === predictions.polePosition) ? 
                          <span className="ml-2 text-green-600">✓</span> : 
                          <span className="ml-2 text-red-600">✗</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">{normalizeDriverNameForDisplay(result.polePosition)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-black">
                        {score.breakdown.find(b => b.type === 'POLE_POSITION')?.points || 0}
                      </td>
                    </tr>
                    
                    {/* Fastest Lap */}
                    <tr>
                      <td className="px-3 py-2 whitespace-nowrap text-black">Fastest Lap</td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">
                        {normalizeDriverNameForDisplay(predictions.fastestLap)}
                        {score.breakdown.some(b => b.type === 'FASTEST_LAP' && b.driver === predictions.fastestLap) ? 
                          <span className="ml-2 text-green-600">✓</span> : 
                          <span className="ml-2 text-red-600">✗</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">{normalizeDriverNameForDisplay(result.fastestLap)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-black">
                        {score.breakdown.find(b => b.type === 'FASTEST_LAP')?.points || 0}
                      </td>
                    </tr>
                    
                    {/* First Retirement */}
                    <tr>
                      <td className="px-3 py-2 whitespace-nowrap text-black">First Retirement</td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">
                        {normalizeDriverNameForDisplay(predictions.firstRetirement)}
                        {score.breakdown.some(b => b.type === 'FIRST_RETIREMENT' && b.driver === predictions.firstRetirement) ? 
                          <span className="ml-2 text-green-600">✓</span> : 
                          <span className="ml-2 text-red-600">✗</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-black">{normalizeDriverNameForDisplay(result.firstRetirement)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-black">
                        {score.breakdown.find(b => b.type === 'FIRST_RETIREMENT')?.points || 0}
                      </td>
                    </tr>
                    
                    {/* Driver Head to Head */}
                    {predictions.driverHeadToHead && (
                      <tr>
                        <td className="px-3 py-2 whitespace-nowrap text-black">Driver H2H</td>
                        <td className="px-3 py-2 text-black">
                          {normalizeDriverNameForDisplay(predictions.driverHeadToHead.driver1)} vs {normalizeDriverNameForDisplay(predictions.driverHeadToHead.driver2)}
                          <br />
                          <span className="text-sm">Winner: {normalizeDriverNameForDisplay(predictions.driverHeadToHead.winner)}</span>
                          {score.breakdown.some(b => b.type === 'DRIVER_H2H') ? 
                            <span className="ml-2 text-green-600">✓</span> : 
                            <span className="ml-2 text-red-600">✗</span>}
                        </td>
                        <td className="px-3 py-2 text-black">
                          {result && result.headToHead && result.headToHead.drivers && (
                            <>
                              {normalizeDriverNameForDisplay(result.headToHead.drivers.driver1.name)} vs {normalizeDriverNameForDisplay(result.headToHead.drivers.driver2.name)}
                              <br />
                              <span className="text-sm">Winner: {result.headToHead.drivers.winner ? normalizeDriverNameForDisplay(result.headToHead.drivers.winner) : "No winner (tie)"}</span>
                            </>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-black">
                          {score.breakdown.find(b => b.type === 'DRIVER_H2H')?.points || 0}
                        </td>
                      </tr>
                    )}
                    
                    {/* Team Head to Head */}
                    {predictions.teamHeadToHead && (
                      <tr>
                        <td className="px-3 py-2 whitespace-nowrap text-black">Team H2H</td>
                        <td className="px-3 py-2 text-black">
                          {predictions.teamHeadToHead.team1} vs {predictions.teamHeadToHead.team2}
                          <br />
                          <span className="text-sm">Winner: {predictions.teamHeadToHead.winner}</span>
                          {score.breakdown.some(b => b.type === 'TEAM_H2H') ? 
                            <span className="ml-2 text-green-600">✓</span> : 
                            <span className="ml-2 text-red-600">✗</span>}
                        </td>
                        <td className="px-3 py-2 text-black">
                          {result && result.headToHead && result.headToHead.teams && (
                            <>
                              {result.headToHead.teams.team1.name} vs {result.headToHead.teams.team2.name}
                              <br />
                              <span className="text-sm">Winner: {result.headToHead.teams.winner || "No winner (tie)"}</span>
                            </>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-black">
                          {score.breakdown.find(b => b.type === 'TEAM_H2H')?.points || 0}
                        </td>
                      </tr>
                    )}

                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="3" className="px-3 py-2 text-right font-medium text-black">Other Points:</td>
                      <td className="px-3 py-2 text-right font-bold text-black">
                        {score.breakdown
                          .filter(item => !['WINNER', 'SECOND_PLACE', 'THIRD_PLACE', 'PODIUM_DRIVER', 'ALL_PODIUM_CORRECT_ORDER', 'ALL_PODIUM_WRONG_ORDER'].includes(item.type))
                          .reduce((sum, item) => sum + item.points, 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            
            {/* Total Score */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-blue-800">Total Score:</span>
                <span className="text-xl font-bold text-blue-800">{score.score} points</span>
              </div>
            </div>
          </div>
        );
      };

  if (loading && !races.length) return <div className="text-center py-8">Loading data...</div>;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-4">Admin Panel</h2>
      
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {message}
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button 
            className={`py-4 px-1 ${activeTab === 'results' ? 'border-b-2 border-blue-500 font-medium text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('results')}
          >
            Process Results
          </button>
          
          {selectedRace && (
            <button 
              className={`py-4 px-1 ${activeTab === 'raceScores' ? 'border-b-2 border-blue-500 font-medium text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('raceScores')}
            >
              {selectedRace} Scores
            </button>
          )}
          {selectedScore && (
            <button 
              className={`py-4 px-1 ${activeTab === 'scoreDetails' ? 'border-b-2 border-blue-500 font-medium text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('scoreDetails')}
            >
              Score Details
            </button>
          )}
          {editingRace && (
            <button 
                className={`py-4 px-1 ${activeTab === 'headToHead' ? 'border-b-2 border-blue-500 font-medium text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('headToHead')}
            >
                Head-to-Head Setup
            </button>
            )}
        </nav>
      </div>
      
        {/* Process Results Tab */}
        {activeTab === 'results' && (
          <div>
            <h3 className="text-lg font-semibold mb-3">Process Race Results</h3>
            
            {/* Global Force Recalculate Option */}
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-yellow-800 mb-2">Scoring Options</h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={Object.keys(forceRecalculate).length > 0 && Object.values(forceRecalculate).every(v => v)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Set force recalculate for all races
                        const newForceRecalc = {};
                        races.forEach(race => {
                          newForceRecalc[race._id] = true;
                        });
                        setForceRecalculate(newForceRecalc);
                      } else {
                        // Clear all force recalculate
                        setForceRecalculate({});
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-yellow-800">
                    <strong>Force Recalculate All Races</strong> - Use this if you've updated scoring logic and need to recalculate existing scores
                  </span>
                </label>
                <p className="text-xs text-yellow-700 ml-6">
                  ⚠️ This will overwrite all existing scores with the updated scoring logic (including bonus points)
                </p>
              </div>
            </div>

            <div className="space-y-4">
            {races.map(race => (
                <div key={race._id} className="border p-3 rounded">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{race.raceName}</h4>
                      <p className="text-sm text-gray-500">
                        {new Date(race.date).toLocaleDateString()}
                        {race.circuit && race.circuit.country && ` - ${race.circuit.country}`}
                      </p>
                      {race.headToHead && (
                        <div className="mt-1 text-xs text-gray-600">
                          <p>
                            <span className="font-medium">Driver H2H:</span>{' '}
                            {race.headToHead.drivers?.driver1?.name || 'Not set'} vs {race.headToHead.drivers?.driver2?.name || 'Not set'}
                          </p>
                          <p>
                            <span className="font-medium">Team H2H:</span>{' '}
                            {race.headToHead.teams?.team1?.name || 'Not set'} vs {race.headToHead.teams?.team2?.name || 'Not set'}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => editHeadToHead(race)}
                        className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-500 rounded"
                      >
                        Set H2H
                      </button>
                      <button
                        onClick={() => fetchRaceScores(race._id, race.raceName)}
                        className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-500 rounded"
                      >
                        View Scores
                      </button>
                      <button
                        onClick={() => processResults(race._id, race.raceName, forceRecalculate[race._id] || false)}
                        className={`text-sm px-3 py-1 border rounded ${
                          forceRecalculate[race._id] 
                            ? 'text-orange-600 hover:text-orange-800 border-orange-500 bg-orange-50' 
                            : 'text-purple-600 hover:text-purple-800 border-purple-500'
                        }`}
                        disabled={loading}
                      >
                        {forceRecalculate[race._id] ? 'Force Recalculate' : 'Process Results'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Individual race force recalculate option */}
                  <div className="mt-2 ml-4">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={forceRecalculate[race._id] || false}
                        onChange={(e) => {
                          setForceRecalculate({
                            ...forceRecalculate,
                            [race._id]: e.target.checked
                          });
                        }}
                        className="mr-2"
                      />
                      <span className="text-gray-600">
                        Force recalculate scores for this race
                      </span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            
            {/* New section for calculating scores */}
            <div className="mt-8 border-t pt-6">
              <h3 className="text-lg font-semibold mb-3">Calculate All User Scores</h3>
              <div className="bg-gray-50 p-4 rounded">
                <p className="mb-4 text-gray-700">
                  After processing race results, use this button to update the total scores for all users on the leaderboard.
                </p>
                
                <button
                  onClick={calculateAllScores}
                  disabled={calculatingScores}
                  className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 disabled:bg-green-300"
                >
                  {calculatingScores ? 'Calculating...' : 'Update All User Scores'}
                </button>
                
                {calculateScoreResult && (
                  <div className="mt-4">
                    <h4 className="font-medium mb-2">Results:</h4>
                    <div className="overflow-auto max-h-64 bg-white p-3 rounded border">
                      {calculateScoreResult.results && (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Username</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total Points</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Correct Predictions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {calculateScoreResult.results.map((result, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2 whitespace-nowrap">{result.username}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-right">{result.totalPoints}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-right">{result.correctPredictions}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      

      
      {/* Race Scores Tab */}
      {activeTab === 'raceScores' && (
        <div>
          <h3 className="text-lg font-semibold mb-3">{selectedRace} Scores</h3>
          {raceScores.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UserName</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {raceScores.map((score, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-black">{score.username}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-black">{score.score}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-black">
                        <button 
                          onClick={() => viewScoreDetails(score)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No scores available for this race yet.</p>
          )}
        </div>
      )}

      {/* Score Details Tab */}
      {activeTab === 'scoreDetails' && selectedScore && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-black">{selectedScore.username}'s Score Breakdown</h3>
            <span className="text-xl font-bold text-blue-600">{selectedScore.score} points</span>
          </div>
        
          {raceResult ? (
            renderCompleteBreakdown(selectedScore, raceResult)
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="font-medium text-gray-700 mb-2">Points Earned</h4>
              {selectedScore.breakdown && renderScoreBreakdown(selectedScore.breakdown)}
            </div>
          )}

          {selectedScore.summaryText && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 mb-2">Full Summary</h4>
              <div className="bg-gray-900 p-4 rounded-lg whitespace-pre-wrap font-mono text-sm">
                {selectedScore.summaryText}
              </div>
            </div>
          )}

          <div className="mt-6">
            <button
              onClick={() => setActiveTab('raceScores')}
              className="bg-gray-900 text-black py-2 px-4 rounded hover:bg-gray-300"
            >
              Back to {selectedRace} Scores
            </button>
          </div>
        </div>
      )}
      {/* Head-to-Head Configuration Tab */}
{activeTab === 'headToHead' && editingRace && (
  <div>
                                  
    <h3 className="text-lg font-semibold mb-3">
      Configure Head-to-Head for {editingRace.raceName}
    </h3>
    
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h4 className="text-md font-medium mb-4">Driver Head-to-Head</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Driver 1 Selection */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">Driver 1</label>
          <select
            value={headToHeadConfig.drivers.driver1.driverId}
            onChange={(e) => handleDriverSelect('driver1', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Select Driver 1</option>
            {drivers.map(driver => (
              <option 
                key={`d1-${driver.driverId}`} 
                value={driver.driverId}
                disabled={driver.driverId === headToHeadConfig.drivers.driver2.driverId}
              >
                {driver.givenName} {driver.familyName} ({driver.code})
              </option>
            ))}
          </select>
        </div>
        
        {/* Driver 2 Selection */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">Driver 2</label>
          <select
            value={headToHeadConfig.drivers.driver2.driverId}
            onChange={(e) => handleDriverSelect('driver2', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Select Driver 2</option>
            {drivers.map(driver => (
              <option 
                key={`d2-${driver.driverId}`} 
                value={driver.driverId}
                disabled={driver.driverId === headToHeadConfig.drivers.driver1.driverId}
              >
                {driver.givenName} {driver.familyName} ({driver.code})
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
    
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h4 className="text-md font-medium mb-4">Team Head-to-Head</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Team 1 Selection */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">Team 1</label>
          <select
            value={headToHeadConfig.teams.team1.constructorId}
            onChange={(e) => handleTeamSelect('team1', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Select Team 1</option>
            {teams.map(team => (
              <option 
                key={`t1-${team.constructorId}`} 
                value={team.constructorId}
                disabled={team.constructorId === headToHeadConfig.teams.team2.constructorId}
              >
                {team.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Team 2 Selection */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">Team 2</label>
          <select
            value={headToHeadConfig.teams.team2.constructorId}
            onChange={(e) => handleTeamSelect('team2', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Select Team 2</option>
            {teams.map(team => (
              <option 
                key={`t2-${team.constructorId}`} 
                value={team.constructorId}
                disabled={team.constructorId === headToHeadConfig.teams.team1.constructorId}
              >
                {team.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
    
    <div className="flex justify-between mt-6">
      <button
        onClick={() => {
          setEditingRace(null);
          setActiveTab('results');
        }}
        className="bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300"
      >
        Cancel
      </button>
      <button
        onClick={saveHeadToHead}
        className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
        disabled={
          !headToHeadConfig.drivers.driver1.driverId ||
          !headToHeadConfig.drivers.driver2.driverId ||
          !headToHeadConfig.teams.team1.constructorId ||
          !headToHeadConfig.teams.team2.constructorId ||
          loading
        }
      >
        {loading ? 'Saving...' : 'Save Head-to-Head Configuration'}
      </button>
    </div>
  </div>
)}
    </div>
    
  );
}


export default AdminPanel;